import * as React from "react";
import { Text } from "@/components/atoms/Text";
import {
  FunctionalCard,
  FunctionalCardContent,
  FunctionalCardHeader,
} from "@/components/molecules/cards/FunctionalCard";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/molecules/ExpandableContent";
import {
  Popover,
  PopoverArrow,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "@/components/molecules/Popover";
import { PriceLineItemTooltipTitle } from "@/components/molecules/PriceLineItem/PriceLineItemTooltipTitle";
import ChevronDown16Icon from "@/icons/__generated__/ChevronDown/ChevronDown16";
import ChevronDown20Icon from "@/icons/__generated__/ChevronDown/ChevronDown20";
import QuestionIcon from "@/icons/__generated__/Question/Question16";
import XIcon from "@/icons/__generated__/X/X16";
import { cn } from "@/lib/utils";
import { PriceSummarySkeleton } from "./PriceSummarySkeleton";

type PriceSummaryLineItemTooltip =
  | {
      title?: React.ReactNode;
      content: React.ReactNode;
      renderContent?: never;
    }
  | {
      /**
       * Rendered as the popover/drawer heading rather than by `renderContent`,
       * so custom bodies stay free of their own heading.
       */
      title?: React.ReactNode;
      content?: never;
      renderContent: (setOpen: (open: boolean) => void) => React.ReactNode;
    };

type PriceSummaryLineItemProps = {
  name: React.ReactNode;
  value: React.ReactNode;
  description?: React.ReactNode;
  variant?: "default" | "highlight";
  border?: "top" | "bottom" | "both";
  tooltip?: PriceSummaryLineItemTooltip;
};

type PriceSummaryProps = {
  children?: React.ReactNode;
  lineItems?: PriceSummaryLineItemProps[];
  total?: PriceSummaryLineItemProps;
  caption?: React.ReactNode;
  footer?: React.ReactNode;
  footerClassName?: string;
  textSize?: "normal" | "large";
  className?: string;
  showSkeleton?: boolean;
  spacing?: 4 | 8 | 12 | 16;
  testId?: string;
  title?: React.ReactNode;
};

const PriceSummaryContext = React.createContext<{
  spacing: 4 | 8 | 12 | 16;
  textSize: "normal" | "large";
}>({
  spacing: 12,
  textSize: "normal",
});

const PriceSummary = React.forwardRef<HTMLTableElement, PriceSummaryProps>(
  (
    {
      children,
      lineItems,
      total,
      caption,
      footer,
      textSize = "normal",
      className,
      footerClassName,
      showSkeleton = false,
      spacing = 12,
      testId,
      title,
    },
    ref,
  ) => {
    if (showSkeleton) {
      return <PriceSummarySkeleton />;
    }

    return (
      <PriceSummaryContext.Provider value={{ spacing, textSize }}>
        <div data-testid={testId} ref={ref}>
          {title && <PriceSummaryTitle>{title}</PriceSummaryTitle>}
          <div
            className={cn(
              "tw-flex tw-flex-col",
              {
                "tw-body-1": textSize === "normal",
                "tw-body-2": textSize === "large",
                "tw-gap-4": spacing === 4,
                "tw-gap-8": spacing === 8,
                "tw-gap-12": spacing === 12,
                "tw-gap-16": spacing === 16,
              },
              className,
            )}
          >
            {children ||
              (lineItems || []).map((item, index) => (
                <PriceSummaryRow key={index} {...item} />
              ))}

            {total && <PriceSummaryTotalRow {...total} />}

            {footer && (
              <PriceSummaryFooter className={footerClassName}>
                {footer}
              </PriceSummaryFooter>
            )}

            {caption && <PriceSummaryCaption>{caption}</PriceSummaryCaption>}
          </div>
        </div>
      </PriceSummaryContext.Provider>
    );
  },
);
PriceSummary.displayName = "PriceSummary";

type PriceSummaryTitleProps = {
  children: React.ReactNode;
  className?: string;
};

const PriceSummaryTitle = ({ children, className }: PriceSummaryTitleProps) => {
  return (
    <Text
      element="div"
      typeface="headline-1-m"
      className={cn("tw-mb-16", className)}
    >
      {children}
    </Text>
  );
};

type PriceSummaryFooterProps = {
  children: React.ReactNode;
  className?: string;
};
const PriceSummaryFooter = ({
  children,
  className,
}: PriceSummaryFooterProps) => {
  return (
    <div
      className={cn(
        "tw-text-left tw-caption-2 tw-text-gray-600 tw-border-t-0",
        className,
      )}
    >
      {children}
    </div>
  );
};

PriceSummaryFooter.displayName = "PriceSummaryFooter";

type PriceSummaryCaptionProps = {
  children: React.ReactNode;
  className?: string;
};
const PriceSummaryCaption = ({
  children,
  className,
}: PriceSummaryCaptionProps) => {
  const { spacing } = React.useContext(PriceSummaryContext);

  return (
    <div
      className={cn(
        "tw-text-left tw-border-t tw-border-gray-300 tw-caption-2",
        {
          "tw-pt-8": spacing === 8,
          "tw-pt-12": spacing === 12,
          "tw-pt-16": spacing === 16,
        },
        className,
      )}
    >
      {children}
    </div>
  );
};
PriceSummaryCaption.displayName = "PriceSummaryCaption";

type PriceSummaryRowTooltipProps = PriceSummaryLineItemTooltip & {
  className?: string;
  closeButton?: React.ReactNode;
};

const PriceSummaryRowTooltip = ({
  title,
  content,
  className,
  closeButton,
}: PriceSummaryRowTooltipProps) => {
  return (
    <FunctionalCard
      className={cn("tw-border-0 tw-w-full sm:tw-w-[225px]", className)}
    >
      {title && (
        <FunctionalCardHeader
          className="sm:tw-border-b-0"
          closeButton={
            closeButton || (
              <PopoverClose>
                <XIcon />
              </PopoverClose>
            )
          }
        >
          <Text typeface="body-1-m">{title}</Text>
        </FunctionalCardHeader>
      )}
      <FunctionalCardContent className="sm:tw-pt-8">
        <Text element="div" typeface="body-1" className="tw-text-gray-900">
          {content}
        </Text>
      </FunctionalCardContent>
    </FunctionalCard>
  );
};

type PriceSummaryRowProps = PriceSummaryLineItemProps & {
  testId?: string;
  className?: string;
};
const PriceSummaryRow = ({
  name,
  value,
  description,
  variant,
  border,
  testId,
  className,
  tooltip,
  ...trProps
}: PriceSummaryRowProps) => {
  const { spacing } = React.useContext(PriceSummaryContext);

  const hasTopBorder = border === "top" || border === "both";
  const hasBottomBorder = border === "bottom" || border === "both";

  const [tooltipOpen, setTooltipOpen] = React.useState(false);

  return (
    <div
      className={cn(
        "tw-grid tw-grid-cols-3 tw-border-0 tw-border-gray-300 tw-gap-8",
        {
          "tw-border-t": border === "top",
          "tw-border-b": border === "bottom",
          "tw-border-t tw-border-b": border === "both",
          "tw-pt-8": spacing === 8 && hasTopBorder,
          "tw-pt-12": spacing === 12 && hasTopBorder,
          "tw-pt-16": spacing === 16 && hasTopBorder,
          "tw-pb-8": spacing === 8 && hasBottomBorder,
          "tw-pb-12": spacing === 12 && hasBottomBorder,
          "tw-pb-16": spacing === 16 && hasBottomBorder,
        },
        className,
      )}
      {...trProps}
    >
      <div
        className="tw-col-span-2"
        data-testid={testId ? `${testId}-name` : undefined}
      >
        {tooltip ? (
          <Popover
            open={tooltipOpen}
            onOpenChange={setTooltipOpen}
            isResponsive={true}
          >
            <PopoverTrigger className="tw-text-left">
              <span className="tw-underline sm:tw-no-underline tw-inline-flex tw-items-center tw-gap-4">
                <span>{name}</span>
                <QuestionIcon
                  className="tw-hidden sm:tw-block tw-min-w-16"
                  name="question-mark"
                />
              </span>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              side="top"
              className="tw-min-h-[193px] sm:tw-min-h-0"
            >
              {tooltip.renderContent ? (
                <>
                  {tooltip.title && (
                    <PriceLineItemTooltipTitle>
                      {tooltip.title}
                    </PriceLineItemTooltipTitle>
                  )}
                  {tooltip.renderContent(setTooltipOpen)}
                </>
              ) : (
                <PriceSummaryRowTooltip
                  title={tooltip.title}
                  content={tooltip.content}
                />
              )}
              <PopoverArrow className="tw-fill-base-white" />
            </PopoverContent>
          </Popover>
        ) : (
          name
        )}
        {description && (
          <div className="tw-caption-2 tw-text-gray-600">{description}</div>
        )}
      </div>
      <div
        className={cn("tw-text-right tw-col-span-1", {
          "tw-text-green-800": "highlight" === variant,
        })}
        data-testid={testId ? `${testId}-value` : undefined}
      >
        {value}
      </div>
    </div>
  );
};
PriceSummaryRow.displayName = "PriceSummaryRow";

type PriceSummaryRowExpandableProps = PriceSummaryRowProps & {
  expandableContent: React.ReactNode;
  expandableContentClassName?: string;
  showCaret?: boolean;
  caretPosition?: "left" | "contentRight" | "right";
};

const PriceSummaryRowExpandable = ({
  expandableContent,
  expandableContentClassName,
  showCaret = true,
  caretPosition = "left",
  name,
  value,
  ...rowProps
}: PriceSummaryRowExpandableProps) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const { textSize } = React.useContext(PriceSummaryContext);

  const caretIcon = showCaret ? (
    <span className="tw-flex tw-items-center">
      {textSize === "normal" ? (
        <ChevronDown16Icon
          name="chevron-down"
          className={cn({
            "tw-rotate-180": isOpen,
          })}
        />
      ) : (
        <ChevronDown20Icon
          name="chevron-down"
          className={cn({
            "tw-rotate-180": isOpen,
          })}
        />
      )}
    </span>
  ) : null;

  const renderNameWithCaret = () => {
    if (caretPosition === "left") {
      return (
        <CollapsibleTrigger className="tw-text-left tw-flex tw-gap-8 tw-items-start">
          {caretIcon}
          <span>{name}</span>
        </CollapsibleTrigger>
      );
    } else if (caretPosition === "contentRight") {
      return (
        <CollapsibleTrigger className="tw-text-left tw-flex tw-gap-8 tw-items-start">
          <span>{name}</span>
          {caretIcon}
        </CollapsibleTrigger>
      );
    } else {
      // caretPosition === "right" - name only, trigger wraps entire row
      return name;
    }
  };

  const renderValueWithCaret = () => {
    if (caretPosition === "right") {
      return (
        <div className="tw-flex tw-gap-8 tw-items-start tw-justify-end">
          <span>{value}</span>
          {caretIcon}
        </div>
      );
    }
    return value;
  };

  const contentPaddingClass =
    showCaret && caretPosition === "left" ? "tw-pl-28" : undefined;

  if (caretPosition === "right") {
    return (
      <Collapsible onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="tw-w-full tw-text-left">
          <PriceSummaryRow
            {...rowProps}
            name={renderNameWithCaret()}
            value={renderValueWithCaret()}
          />
        </CollapsibleTrigger>
        <CollapsibleContent shouldAnimate={true}>
          <div className={cn(contentPaddingClass, expandableContentClassName)}>
            {expandableContent}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <Collapsible onOpenChange={setIsOpen}>
      <PriceSummaryRow
        {...rowProps}
        name={renderNameWithCaret()}
        value={value}
      />
      <CollapsibleContent shouldAnimate={true}>
        <div className={cn(contentPaddingClass, expandableContentClassName)}>
          {expandableContent}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

type PriceSummaryTotalRowProps = Omit<PriceSummaryRowProps, "name"> & {
  name?: React.ReactNode;
};

const PriceSummaryTotalRow = ({
  variant,
  className,
  name = "Total",
  ...rowProps
}: PriceSummaryTotalRowProps) => {
  const { textSize, spacing } = React.useContext(PriceSummaryContext);

  return (
    <PriceSummaryRow
      border="top"
      name={name}
      {...rowProps}
      className={cn(
        {
          "tw-mt-12 tw-pt-16": spacing === 4,
          "tw-mt-8 tw-pt-16": spacing === 8,
          "tw-mt-4 tw-pt-16": spacing === 12,
          "tw-body-1-m": textSize === "normal",
          "tw-body-2-m": textSize === "large",
          "tw-text-green-800": variant === "highlight",
        },
        className,
      )}
    />
  );
};

export {
  PriceSummary,
  PriceSummaryCaption,
  PriceSummaryFooter,
  PriceSummaryRow,
  PriceSummaryRowExpandable,
  PriceSummaryRowTooltip,
  PriceSummaryTitle,
  PriceSummaryTotalRow,
  type PriceSummaryCaptionProps,
  type PriceSummaryFooterProps,
  type PriceSummaryLineItemProps,
  type PriceSummaryProps,
  type PriceSummaryRowExpandableProps,
  type PriceSummaryRowProps,
  type PriceSummaryRowTooltipProps,
  type PriceSummaryTitleProps,
  type PriceSummaryTotalRowProps,
};
